import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { getSafeErrorMessage } from "@/lib/errorUtils";

const contactSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().min(1, "Last name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(1, "Phone number is required").max(20),
  vehicle: z.string().trim().min(1, "Vehicle information is required").max(200),
  service: z.string().min(1, "Please select a service"),
  message: z.string().trim().max(1000).optional(),
});

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setLoading(true);

    try {
      const formData = new FormData(form);
      const data = {
        firstName: formData.get("firstName") as string,
        lastName: formData.get("lastName") as string,
        email: formData.get("email") as string,
        phone: formData.get("phone") as string,
        vehicle: formData.get("vehicle") as string,
        service: formData.get("service") as string,
        message: formData.get("message") as string || "",
      };

      // Validate
      const validation = contactSchema.safeParse(data);
      if (!validation.success) {
        toast({
          title: "Validation Error",
          description: validation.error.errors[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Submit to database
      const { error } = await supabase.from("contact_submissions").insert({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        vehicle: data.vehicle,
        service: data.service,
        message: data.message,
      });

      if (error) throw error;

      // Send email notification
      try {
        await supabase.functions.invoke("send-contact-notification", {
          body: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            vehicle: data.vehicle,
            service: data.service,
            message: data.message,
          },
        });
      } catch (emailError) {
        console.error("Email notification failed:", emailError);
        // Don't show error to user - submission is saved
      }

      // Redirect to thank you page for conversion tracking
      navigate("/thank-you");
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact | Battle Born Protection Films | Las Vegas PPF & Ceramic Coating"
        description="Contact Battle Born Protection Films. Schedule a free PPF or ceramic coating quote. 6960 W Russell Rd B, Las Vegas. Mon–Fri 8–5. (702) 559-9365."
        canonical="https://battlebornppf.com/contact"
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Request Your Free Quote
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get in touch with our team to discuss your vehicle's protection needs
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Contact Form */}
            <Card className="lg:col-span-2 bg-card border-border">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        id="firstName"
                        name="firstName"
                        placeholder="John" 
                        required 
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        id="lastName"
                        name="lastName"
                        placeholder="Doe" 
                        required 
                        className="bg-background"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input 
                        id="email"
                        name="email"
                        type="email" 
                        placeholder="john@example.com" 
                        required 
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone"
                        name="phone"
                        type="tel" 
                        placeholder="(702) 559-9365" 
                        required 
                        className="bg-background"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicle">Vehicle Year, Make, Model *</Label>
                    <Input 
                      id="vehicle"
                      name="vehicle"
                      placeholder="2024 Porsche 911 Turbo S" 
                      required 
                      className="bg-background"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">Service Interest *</Label>
                    <select 
                      id="service"
                      name="service"
                      required
                      className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground"
                    >
                      <option value="">Select a service...</option>
                      <option value="ppf">Paint Protection Film</option>
                      <option value="ceramic">Ceramic Coating</option>
                      <option value="ceramic-window-tint">Ceramic Window Tint</option>
                      <option value="vinyl-wraps">Vinyl Wraps & Graphics</option>
                      <option value="windshield-protection">Windshield Protection</option>
                      <option value="multiple">Multiple Services</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Details</Label>
                    <Textarea 
                      id="message"
                      name="message"
                      placeholder="Tell us about your project and any specific requirements..."
                      rows={5}
                      className="bg-background"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    variant="premium" 
                    size="lg" 
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Quote Request"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-6">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Location</p>
                        <p className="text-sm text-muted-foreground">
                          Battle Born Protection Films<br />
                          6960 W Russell Rd B<br />
                          Las Vegas, NV 89113
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Phone</p>
                        <a 
                          href="tel:+17025599365" 
                          className="text-sm text-muted-foreground hover:text-accent transition-colors"
                        >
                          (702) 559-9365
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Mail className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Email</p>
                        <a 
                          href="mailto:battlebornppf@gmail.com" 
                          className="text-sm text-muted-foreground hover:text-accent transition-colors"
                        >
                          battlebornppf@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Clock className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Hours</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Mon–Fri: 9:00 AM – 5:00 PM</p>
                          <p>Sat: 10:00 AM – 3:00 PM</p>
                          <p>Sun: By Appointment Only</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4">What to Expect</h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-accent">•</span>
                      <span>Response within 24 hours</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-accent">•</span>
                      <span>Detailed quote and timeline</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-accent">•</span>
                      <span>Free consultation available</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-accent">•</span>
                      <span>Flexible scheduling options</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-heading font-bold mb-8 text-center">Visit Our Facility</h2>
            <div className="rounded-lg overflow-hidden border border-border" style={{ height: "450px" }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3224.326132404586!2d-115.2468932!3d36.0855372!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x68f8d021666f5c25%3A0x59c525cb19a438bc!2sBattle%20Born%20Protection%20Films!5e0!3m2!1sen!2sin!4v1760709391676!5m2!1sen!2sin" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Battle Born Protection Films Location"
              />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
